// Route Code Edited 08/28/2026
/**
 * Route Dialog Handler
 *
 * Patches HTMLDialogElement.prototype.showModal/.close so the cart flyout
 * dialog (id^="CartFlyout-sections--") opens via show() instead of
 * showModal(). showModal() promotes the dialog into the top layer, which
 * renders above Route's own modal — show() doesn't, so we add a synthetic
 * backdrop + positioning CSS to replicate the showModal() look.
 *
 * Because show() skips native dialog behavior, we replicate what's needed:
 *  - Escape closes the flyout (global keydown listener).
 *  - Focus moves in on open, and back to the trigger on close.
 *  - modal-dialog-component treats any outside click as a reason to close.
 *    Route's modal renders outside the dialog's DOM, so clicking it looks
 *    like an outside click and closes the flyout too. We can't
 *    stopPropagation (that would also block Route's modal from getting the
 *    click), so a window-capture listener flags the click instead, and our
 *    patched close() no-ops for that one call.
 *  - <header data-hidden> auto-hides via `translate: 0 -100%`, which makes
 *    it the containing block for the fixed-position dialog inside it — if
 *    the header is mid-hide when the flyout opens, the dialog gets clipped.
 *    We clear data-hidden/inert first (transition suppressed so the
 *    containing-block change is instant, not animated).
 *  - <cart-flyout-component> ships empty and fills async, and can still be
 *    empty when this fires on a busy page load — so we wait for it to gain
 *    children (bounded to 500ms) before opening.
 *
 * Usage:
 *   Add this script tag at the bottom of layout/theme.liquid (before </body>):
 *   <!-- Route Code Edited 08/28/2026 -->
 *   <script src="{{ 'route-dialog-handler.js' | asset_url }}" defer></script>
 *   <!-- End Route Code -->
 *
 */
(function () {
  const _showModal = HTMLDialogElement.prototype.showModal;
  const _close = HTMLDialogElement.prototype.close;
  const CART_FLYOUT_READY_TIMEOUT_MS = 500;
  const ROUTE_MODAL_SELECTOR = '.route-modal';
  const returnFocusTargets = new WeakMap();
  let ignoreNextCartFlyoutClose = false;

  function isCartFlyout(dialog) {
    return !!(dialog.id && dialog.id.startsWith('CartFlyout-sections--'));
  }

  function focusFirstIn(dialog) {
    const target = dialog.querySelector('[autofocus]') || dialog.querySelector('.button-flyout');
    target?.focus();
  }

  // Resolves once <cart-flyout-component> has been populated by the theme's
  // own loadCart() fetch, or after a bounded timeout if that never happens.
  function waitForCartFlyoutReady(dialog) {
    const cartComponent = dialog.querySelector('cart-flyout-component');
    if (!cartComponent || cartComponent.childElementCount > 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      };
      const observer = new MutationObserver(() => {
        if (cartComponent.childElementCount > 0) settle();
      });
      observer.observe(cartComponent, { childList: true });
      const timer = setTimeout(settle, CART_FLYOUT_READY_TIMEOUT_MS);
    });
  }

  function openCartFlyout(dialog) {
    if (!document.getElementById('non-modal-drawer-cart-styles')) {
      const style = document.createElement('style');
      style.id = 'non-modal-drawer-cart-styles';
      style.textContent = `
        dialog[open].flyout-right {
          top: 0 !important;
          z-index: 2147483647;
        }
        .cart-backdrop {
          position: fixed;
          inset: 0;
          z-index: 8;
          pointer-events: none;
          background: rgba(0, 0, 0, 0.375);
          opacity: 0;
          transition: none !important;
        }
        .cart-backdrop:has(+ dialog.flyout-right[open]) {
          opacity: 1;
          pointer-events: auto;
        }
        sticky-header-component:has(dialog.flyout-right[open]) {
          contain: style;
        }`;
      document.head.appendChild(style);
    }

    // Ensure backdrop exists directly before the dialog in the DOM
    let backdrop = dialog.parentNode.querySelector(':scope > div.cart-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.classList.add('cart-backdrop');
    }
    if (dialog.previousElementSibling !== backdrop) {
      dialog.parentNode.insertBefore(backdrop, dialog);
    }

    // Clear any scroll-driven auto-hide state on <header> before revealing the
    // dialog — a translated/inert header would otherwise become the fixed
    // dialog's containing block and truncate it. <header> has
    // `transition-transform duration-300`, so just removing the attribute
    // would animate the translate back to 0 over 300ms — and the dialog stays
    // mis-sized for the whole animation, since the containing block only
    // clears once translate finally reaches its resting value. Suppress the
    // transition for this one change so it applies instantly.
    const header_el = document.querySelector('header');
    if (header_el) {
      const prevTransitionProperty = header_el.style.transitionProperty;
      header_el.style.transitionProperty = 'none';
      header_el.removeAttribute('data-hidden');
      header_el.removeAttribute('inert');
      header_el.getBoundingClientRect(); // flush the style change before transitions come back
      header_el.style.transitionProperty = prevTransitionProperty;
    }

    returnFocusTargets.set(dialog, document.activeElement);

    dialog.show();
    header_el?.setAttribute('data-flyout-open', '');
    document.body?.setAttribute('data-noscroll', '1');
    focusFirstIn(dialog);

    if (!dialog.dataset.routeCloseBound) {
      dialog.dataset.routeCloseBound = '1';
      dialog.addEventListener('close', () => {
        document.querySelector('header')?.removeAttribute('data-flyout-open');
        document.body?.removeAttribute('data-noscroll');

        const returnFocusTo = returnFocusTargets.get(dialog);
        returnFocusTargets.delete(dialog);
        if (returnFocusTo instanceof HTMLElement && document.contains(returnFocusTo)) {
          returnFocusTo.focus();
        }
      });
    }
  }

  // modal-dialog-component has no Escape handling of its own — it relies on
  // showModal()'s native cancel-on-Escape behavior, which show() doesn't provide.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelector('dialog[open][id^="CartFlyout-sections--"]')?.close();
  });

  // Flag clicks that land inside Route's modal, before modal-dialog-component's
  // own document-capture click listener runs and calls dialog.close().
  window.addEventListener('click', (e) => {
    if (!e.target || !e.target.closest || !e.target.closest(ROUTE_MODAL_SELECTOR)) return;
    ignoreNextCartFlyoutClose = true;
    setTimeout(() => { ignoreNextCartFlyoutClose = false; }, 0);
  }, true);

  HTMLDialogElement.prototype.showModal = function () {
    if (isCartFlyout(this)) {
      if (this.open || this.dataset.routeOpening === '1') return;
      this.dataset.routeOpening = '1';
      waitForCartFlyoutReady(this).then(() => {
        delete this.dataset.routeOpening;
        if (!this.open) openCartFlyout(this);
      });
    } else {
      _showModal.call(this);
    }
  };

  HTMLDialogElement.prototype.close = function (...args) {
    if (isCartFlyout(this) && ignoreNextCartFlyoutClose) {
      ignoreNextCartFlyoutClose = false;
      return;
    }
    return _close.apply(this, args);
  };
})();
